<template>
  <div class="products-export">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" />

    <div class="actions">
      <artmisads-button isSquare="true" type="primary" @click="onCreateTask">
        创建导出任务
      </artmisads-button>
    </div>

    <div class="table-wrap">
      <artmisads-table
        v-model:pagination="pagination"
        :columns="columns"
        :data-source="taskList"
        :loading="loading"
        :change="onTableChange"
        :scroll="{ x: 900 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <span :class="['status-text', getStatusMeta(record.status).className]">
              {{ getStatusMeta(record.status).text }}
            </span>
          </template>
          <template v-if="column.key === 'action'">
            <a
              v-if="getActionMeta(record).clickable"
              href="javascript:void(0)"
              class="action-link"
              @click="onActionClick(record)"
            >
              {{ getActionMeta(record).text }}
            </a>
            <span v-else class="action-placeholder">
              {{ getActionMeta(record).text }}
            </span>
          </template>
        </template>
      </artmisads-table>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import moment from 'moment'
import { message } from 'ant-design-vue'
import { useRouter } from 'vue-router'
import breadcrumbs from '../../breadcrumbs.vue'
import { productsExportApi } from '~/server/operator/productsExport'

const router = useRouter()

const breadcrumbsData = ref({
  title: 'Products Export',
  des: '',
  links: [
    {
      name: 'Home',
      path: '/operator/users',
    },
    {
      name: 'Operation',
      path: '/operator/operation/manualPayments',
    },
    {
      name: 'Products Export',
      path: '/operator/operation/productsExport',
    },
  ],
})

const columns = [
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    align: 'center',
  },
  {
    title: '文件名',
    dataIndex: 'fileName',
    key: 'fileName',
    align: 'center',
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    align: 'center',
  },
  {
    title: '操作人',
    dataIndex: 'operatorName',
    key: 'operator',
    align: 'center',
  },
  {
    title: '操作',
    dataIndex: 'action',
    key: 'action',
    align: 'center',
  },
]

const loading = ref(false)
const taskList = ref([])
const pagination = reactive({
  current: 1,
  defaultPageSize: 10,
  size: 10,
  pageSize: 10,
  total: 0,
  pageSizeOptions: ['10', '20', '50', '100'],
  showSizeChanger: true,
})

const STATUS_META = {
  WAITING: { text: '等待中', className: 'status-waiting' },
  GENERATING: { text: '生成中', className: 'status-processing' },
  GENERATED: { text: '已生成', className: 'status-success' },
  FAILED: { text: '生成失败', className: 'status-failed' },
  CANCELLED: { text: '已取消', className: 'status-cancelled' },
}

const normalizeTimestamp = (value) => {
  if (!value) return '-'
  const timestamp = Number(value)
  if (Number.isNaN(timestamp)) return '-'
  const ms = timestamp < 1000000000000 ? timestamp * 1000 : timestamp
  return moment(ms).format('YYYY-MM-DD HH:mm:ss')
}

const getStatusMeta = (status) => {
  if (!status) return { text: '-', className: 'status-queued' }
  return STATUS_META[String(status).toUpperCase()] || { text: status, className: 'status-queued' }
}

const normalizeFileUrl = (url) => {
  const trimmed = String(url || '').trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('//')) return `${window.location.protocol}${trimmed}`
  return `${window.location.origin}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  }
}

const downloadByFileUrl = async (record) => {
  const fileUrl = normalizeFileUrl(record.fileUrl)
  if (!fileUrl) {
    message.error('文件不存在')
    return
  }
  const copied = await copyToClipboard(fileUrl)
  if (copied) {
    message.success('下载链接已复制，请在浏览器地址栏粘贴打开下载')
  } else {
    message.warning(`复制失败，请手动复制链接：${fileUrl}`)
  }
}

const getActionMeta = (record) => {
  const status = String(record.status || '').toUpperCase()
  if (status === 'WAITING') return { text: '取消', clickable: true,key: 'cancel' }
  if (status === 'GENERATED') return { text: '下载', clickable: true, key: 'download' }
  if (status === 'FAILED') return { text: '重试', clickable: true,key: 'retry' }
  return { text: '--', clickable: false }
}

const onActionClick = (record) => {
  const action = getActionMeta(record)  
  if (!action.clickable) return
  if (action.key === 'download') {
    downloadByFileUrl(record)
  }
  if(action.key === 'cancel') {
    productsExportApi.cancelTask({
      id: record.id,
    }).then(res => {
      if(res.success) {
        message.success('取消成功')
        fetchTaskList()
      } else {
        message.error(res.message)
      }
    })
  }
  if(action.key === 'retry') {
    productsExportApi.retryTask({
      id: record.id,
    }).then(res => {
      if(res.success) {
        message.success('重试成功')
        fetchTaskList()
      } else {
        message.error(res.message)
      }
    })
  }

}

const fetchTaskList = async (params = {}) => {
  loading.value = true
  if (Object.keys(params).length === 0) {
    pagination.current = 1
  }
  const defaultParams = {
    page: 1,
    size: pagination.size,
  }
  const finalParams = { ...defaultParams, ...params }
  try {
    const res = await productsExportApi.getSellerConnections(finalParams)

    const list = res?.data?.data || []
    const total = res?.data?.total || 0
    taskList.value = list.map((item) => ({
      ...item,
      key: item.id,
      createdAt: normalizeTimestamp(item.createdAt),
    }))
    pagination.total = total
  } finally {
    loading.value = false
  }
}

const onTableChange = ({ current, pageSize }) => {
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  fetchTaskList({ page: current, size: pageSize })
}

const onCreateTask = () => {
  router.push('/operator/operation/productsExport/create')
}

onMounted(() => {
  fetchTaskList({})
})
</script>

<style scoped lang="less">
.products-export {
  width: 100%;
  padding: 16px;
  padding-top: unset;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
}

.table-wrap {
  padding: 15px;
  overflow: hidden;
  border: 1px solid #ffffff;
  border-radius: 12px;
  background-color: var(--color-white);

  :deep(.ant-table) {
    border-radius: 8px 8px 0 0;
  }
}

.status-text {
  font-weight: 500;
}

.status-waiting {
  color: #8c8c8c;
}

.status-processing {
  color: #7653da;
}

.status-success {
  color: #7653da;
}

.status-failed {
  color: #eb4e3d;
}

.status-cancelled {
  color: #eb4e3d;
}

.action-link {
  color: var(--primary);

  &:hover {
    color: var(--primary-hover);
  }
}

.action-placeholder {
  color: var(--dark-3);
}
</style>